import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import * as React from "react";
import { CustomDialogProps } from "../dialog.interface";

export default function AlertDialog({
  dialogState,
  setDialogState,
}: CustomDialogProps) {
  /*
    The confirm button owns its own pending state.

    `dialogState.buttonLoading` cannot supply it. Callers build the whole dialog
    object inside the click handler — `setDialogState({ ..., buttonLoading:
    runAudienceLoading })` — so the flag is a SNAPSHOT taken before the request
    starts, and it is false every time. Nothing re-runs `setDialogState` when the
    mutation flips, so the spinner never appeared however long the call took.
    The flag is still honoured for any caller that does keep it live.
  */
  const [isPending, setIsPending] = React.useState(false);
  const isLoading = isPending || (dialogState.buttonLoading ?? false);

  // A fresh dialog must never open mid-spin, whatever the last one left behind.
  React.useEffect(() => {
    if (!dialogState.open) setIsPending(false);
  }, [dialogState.open]);

  /*
    Closing is blocked on `isPending` ALONE, never on `isLoading`. `isPending`
    always clears in the `finally` below; `buttonLoading` does not — several
    callers raise it through `useLoadingDialog` and only lower it by closing the
    dialog, so a confirm handler that rejects without a catch leaves it stuck
    true. Gating the dismiss on that would make such a dialog unclosable.
  */
  const handleClose = () => {
    if (isPending) return;
    setDialogState({ open: false, title: "" });
  };

  const handleConfirm = async () => {
    if (isLoading) return;
    setIsPending(true);
    try {
      // Confirm handlers are async and swallow their own errors, closing the
      // dialog themselves on success. Awaiting is what makes the spinner track
      // the real request rather than a single frame.
      await dialogState.buttonFunction?.(dialogState?.buttonFunctionParams);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog
      open={Boolean(dialogState.open && dialogState.title)}
      onClose={handleClose}
      fullWidth
      sx={{
        ".MuiDialog-paper": {
          maxWidth: "512px",
          height: "300px",
        },
        ...(dialogState.styles ?? {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ErrorOutlineIcon
            sx={{ height: "150px", width: "150px" }}
            color="secondary"
          />
        </Box>
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="h3Semi">{dialogState.title}</Typography>

          <Typography variant="body1Reg" color="common.black">
            {dialogState.children}
          </Typography>
        </Box>

        <DialogActions>
          {/* custom3 (muted) for dismiss, custom1 (filled) for the action —
              the pairing every other modal in the app uses. Both were
              CustomLoadingButton on the custom1 default, so the two choices
              looked identical and neither read as primary. */}
          <Button variant="custom3" onClick={handleClose} disabled={isPending}>
            {dialogState.cancelButtonName ?? "dismiss"}
          </Button>
          <CustomLoadingButton
            variant="custom1"
            isLoading={isLoading}
            disabled={isLoading}
            title={dialogState.buttonName ?? "Approve"}
            onClick={handleConfirm}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
}
